/**
 * Checkout Operations - Shared Types
 *
 * Types and mapping utilities shared between OrderApp and Saleor operations.
 */

import type { OrderAppAddressInput } from '@/graphql/orderApp/api';
import type { SaleorAddressInput, SaleorCheckoutFragment } from '@/graphql/saleor/api';
import { getCurrencyFromChannel } from '@/lib/configuration/channels';
import type { AddressMetadataEntry } from '../addressMetadata';
import type { ApiError, CheckoutAddress, CheckoutData } from '../types';

// Re-export types that other modules need
export type { ApiError, CheckoutData, CheckoutLineInput } from '../types';

// ============================================
// Constants
// ============================================

export const DEFAULT_LANGUAGE_CODE = 'EN_US';
export const THUMBNAIL_SIZE = 100;
export const IMAGES_FORMAT = 'WEBP' as const;

// Valid OrderApp language codes
export const ORDERAPP_LANGUAGE_CODES = [
  'DE',
  'EN_GB',
  'EN_US',
  'ES',
  'FR',
  'IT',
  'JA',
  'KO',
  'PT',
  'RU',
  'ZH_HANT',
  'ZH_HANT_TW',
] as const;
export type OrderAppLanguageCode = (typeof ORDERAPP_LANGUAGE_CODES)[number];

// ============================================
// Language Code Normalization
// ============================================

/**
 * Normalize language code for OrderApp
 * OrderApp expects codes like EN_US, EN_GB, DE, FR, etc.
 * Input could be 'EN', 'EN_US', 'en-US', 'en', etc.
 */
export function normalizeLanguageCodeForOrderApp(input?: string): OrderAppLanguageCode {
  if (!input) return 'EN_US';

  // Normalize: uppercase and replace hyphens with underscores
  const normalized = input.toUpperCase().replace(/-/g, '_');

  // Direct match
  if (ORDERAPP_LANGUAGE_CODES.includes(normalized as OrderAppLanguageCode)) {
    return normalized as OrderAppLanguageCode;
  }

  // Map simple codes to full codes
  const languageMap: Record<string, OrderAppLanguageCode> = {
    EN: 'EN_US',
    DE: 'DE',
    ES: 'ES',
    FR: 'FR',
    IT: 'IT',
    JA: 'JA',
    KO: 'KO',
    PT: 'PT',
    RU: 'RU',
    ZH: 'ZH_HANT',
    // WARNING: OrderApp does not currently support Simplified Chinese (ZH_HANS).
    // Mapping to Traditional Chinese (ZH_HANT) as fallback.
    // This affects mainland China users who may see Traditional Chinese content.
    // TODO: Add ZH_HANS support in OrderApp to fix this properly.
    ZH_HANS: 'ZH_HANT',
  };

  // Try direct mapping
  if (languageMap[normalized]) {
    return languageMap[normalized];
  }

  // Try extracting base language (e.g., EN_GB -> EN)
  const baseLanguage = normalized.split('_')[0];
  if (languageMap[baseLanguage]) {
    return languageMap[baseLanguage];
  }

  // Default to EN_US
  return 'EN_US';
}

// ============================================
// Error Mappers
// ============================================

type GraphQLErrorLike = {
  field?: string | null;
  code?: string | null;
  message?: string | null;
  context?: string | null;
};

function parseOrderAppContextJson(context?: string | null): Record<string, unknown> | undefined {
  if (!context) {
    return undefined;
  }

  try {
    return JSON.parse(context) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Map Saleor / OrderApp GraphQL errors to our ApiError type
 */
export function mapApiError(error: GraphQLErrorLike): ApiError {
  const parsedContext = parseOrderAppContextJson(error.context ?? null);

  return {
    field: error.field ?? undefined,
    code: error.code ?? 'UNKNOWN_ERROR',
    message: error.message ?? 'An error occurred',
    ...(parsedContext ? { context: parsedContext } : {}),
  };
}

// ============================================
// Address Mappers
// ============================================

/**
 * Map Saleor address to our CheckoutAddress type
 */
export function mapAddress(
  address: SaleorCheckoutFragment['shippingAddress']
): CheckoutAddress | null {
  if (!address) return null;

  return {
    id: address.id,
    firstName: address.firstName,
    lastName: address.lastName,
    companyName: address.companyName ?? undefined,
    streetAddress1: address.streetAddress1,
    streetAddress2: address.streetAddress2,
    city: address.city,
    cityArea: address.cityArea ?? '',
    postalCode: address.postalCode,
    country: {
      code: address.country.code,
      country: address.country.country,
    },
    countryArea: address.countryArea,
    phone: address.phone ?? undefined,
    metadata: address.metadata?.map(({ key, value }) => ({ key, value })) ?? [],
  };
}

/** Address metadata entry used in checkout address updates. */
export type CheckoutAddressMetadataEntry = AddressMetadataEntry;

/**
 * Shared checkout address payload for shipping/billing updates.
 * Passed through `mapToOrderAppAddress` / `mapToSaleorAddress` to OrderApp and Saleor.
 * Includes optional `metadata` (e.g. `saved: TRUE` for registered-user deferred save).
 */
export type CheckoutAddressPayload = {
  firstName: string;
  lastName: string;
  companyName?: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  cityArea?: string;
  postalCode: string;
  country: string;
  countryArea?: string;
  phone?: string;
  skipValidation?: boolean;
  metadata?: CheckoutAddressMetadataEntry[];
};

/**
 * Map our address input to Saleor AddressInput.
 * Forwards `metadata` when present (required for `saved` flag on billing updates).
 */
export function mapToSaleorAddress(address: CheckoutAddressPayload): SaleorAddressInput {
  return {
    firstName: address.firstName,
    lastName: address.lastName,
    companyName: address.companyName,
    streetAddress1: address.streetAddress1,
    streetAddress2: address.streetAddress2 || '',
    city: address.city,
    cityArea: address.cityArea ?? '',
    postalCode: address.postalCode,
    // oxlint-disable-next-line typescript/no-explicit-any
    country: address.country as any, // CountryCode enum
    countryArea: address.countryArea || '',
    phone: address.phone || '',
    skipValidation: address.skipValidation ?? false,
    ...(address.metadata?.length ? { metadata: address.metadata } : {}),
  };
}

/**
 * Map our address input to OrderApp AddressInput.
 * Forwards `metadata` when present (required for `saved` flag on shipping updates).
 */
export function mapToOrderAppAddress(address: CheckoutAddressPayload): OrderAppAddressInput {
  return {
    firstName: address.firstName,
    lastName: address.lastName,
    companyName: address.companyName,
    streetAddress1: address.streetAddress1,
    streetAddress2: address.streetAddress2,
    city: address.city,
    postalCode: address.postalCode,
    // oxlint-disable-next-line typescript/no-explicit-any
    country: address.country as any, // CountryCode enum
    countryArea: address.countryArea,
    phone: address.phone,
    skipValidation: address.skipValidation ?? false,
    ...(address.metadata?.length ? { metadata: address.metadata } : {}),
  };
}

// ============================================
// Checkout Mapper
// ============================================

/**
 * Map Saleor checkout to our CheckoutData type
 */
export function mapCheckoutToData(checkout: SaleorCheckoutFragment): CheckoutData {
  return {
    id: checkout.id,
    email: checkout.email,
    user: checkout.user
      ? {
          id: checkout.user.id,
          email: checkout.user.email,
          firstName: checkout.user.firstName,
          lastName: checkout.user.lastName,
        }
      : null,
    channel: {
      id: checkout.channel.slug, // Using slug as ID since id is not public
      slug: checkout.channel.slug,
      name: checkout.channel.slug, // Not available without auth
      currencyCode: getCurrencyFromChannel(checkout.channel.slug),
    },
    lines: checkout.lines.map((line) => {
      return {
        id: line.id,
        quantity: line.quantity,
        variant: line.variant
          ? {
              id: line.variant.id,
              name: line.variant.name ?? '',
              sku: line.variant.sku ?? '',
              pricing: null, // Pricing is on line.unitPrice, not variant
              product: line.variant.product
                ? {
                    id: line.variant.product.id,
                    name: line.variant.product.name,
                    slug: line.variant.product.slug,
                    thumbnail: line.variant.product.thumbnail
                      ? {
                          url: line.variant.product.thumbnail.url,
                          alt: line.variant.product.thumbnail.alt ?? '',
                        }
                      : null,
                    productType: line.variant.product.productType
                      ? {
                          id: line.variant.product.productType.id,
                          name: line.variant.product.productType.name,
                          slug: line.variant.product.productType.slug,
                        }
                      : null,
                    // Watch attributes for upsells
                    caseMaterial: line.variant.product.caseMaterial ?? null,
                    color: line.variant.product.color ?? null,
                    size: line.variant.product.size ?? null,
                    compatibleStraps: line.variant.product.compatibleStraps ?? null,
                    warrantyExtensionDuration:
                      line.variant.product.warrantyExtensionDuration ?? null,
                    strapColor: line.variant.product.strapColor ?? null,
                  }
                : null,
            }
          : null,
        unitPrice: {
          currency: line.unitPrice.currency,
          gross: line.unitPrice.gross,
          net: line.unitPrice.net,
          tax: line.unitPrice.tax,
        },
        totalPrice: {
          currency: line.totalPrice.currency,
          gross: line.totalPrice.gross,
          net: line.totalPrice.net,
          tax: line.totalPrice.tax,
        },
        // Metafields for related products
        relatedProductBuckleId: line.relatedProductBuckleId ?? null,
        relatedProductStrapId: line.relatedProductStrapId ?? null,
        relatedProductWatchId: line.relatedProductWatchId ?? null,
        strapPackReference: line.strapPackReference ?? null,
        hasWarrantyExtension: line.hasWarrantyExtension ?? null,
        relatedBraceletSize: line.relatedBraceletSize ?? null,
        // Metadata array for other line metadata
        metadata: line.metadata?.map((m) => ({ key: m.key, value: m.value })) ?? [],
      };
    }),
    shippingAddress: mapAddress(checkout.shippingAddress),
    billingAddress: mapAddress(checkout.billingAddress),
    shippingMethods: checkout.shippingMethods.map((method) => ({
      id: method.id,
      name: method.name,
      price: {
        amount: method.price.amount,
        currency: method.price.currency,
      },
      minimumDeliveryDays: method.minimumDeliveryDays ?? undefined,
      maximumDeliveryDays: method.maximumDeliveryDays ?? undefined,
    })),
    deliveryMethod:
      checkout.deliveryMethod?.__typename === 'ShippingMethod'
        ? {
            __typename: 'ShippingMethod',
            id: checkout.deliveryMethod.id,
            name: checkout.deliveryMethod.name,
          }
        : checkout.deliveryMethod?.__typename === 'Warehouse'
          ? {
              __typename: 'Warehouse',
              id: checkout.deliveryMethod.id,
              name: checkout.deliveryMethod.name,
            }
          : null,
    isShippingRequired: checkout.isShippingRequired,
    voucherCode: checkout.voucherCode,
    discount: checkout.discount
      ? {
          amount: checkout.discount.amount,
          currency: checkout.discount.currency,
        }
      : null,
    discountName: checkout.discountName,
    subtotalPrice: {
      currency: checkout.subtotalPrice.currency,
      gross: checkout.subtotalPrice.gross,
      net: checkout.subtotalPrice.net,
      tax: checkout.subtotalPrice.tax,
    },
    shippingPrice: {
      currency: checkout.shippingPrice.currency,
      gross: checkout.shippingPrice.gross,
      net: checkout.shippingPrice.net,
      tax: checkout.shippingPrice.tax,
    },
    totalPrice: {
      currency: checkout.totalPrice.currency,
      gross: checkout.totalPrice.gross,
      net: checkout.totalPrice.net,
      tax: checkout.totalPrice.tax,
    },
    totalBalance: checkout.totalBalance
      ? {
          amount: checkout.totalBalance.amount,
          currency: checkout.totalBalance.currency,
        }
      : null,
    isGift: checkout.isGift === 'true',
    giftFrom: checkout.giftFrom ?? null,
    giftMessage: checkout.giftMessage ?? null,
    orderType: checkout.orderType ?? null,
    preOrderData: checkout.preOrderData ?? null,
  };
}
